/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import BackButton from "../../../components/BackButton";
import EmptyState from "../../../components/EmptyState";
import Card from "../../../components/Card";
import SubMenu from "../../../components/SubMenu";
import Layout from "../../../layout";
import Withdraw from "./Withdraw";
import "./index.scss";
import { getAccountBalance } from "../../../redux/actions/walletActions";
import { getCurrencyRates } from "../../../redux/actions/currencyRatesActions";
import { numberWithCommas, getCurrencySymbol } from "../../../utils/helpers";
import Carousel from "react-bootstrap/Carousel";
import wbbg from "../../../assets/images/wb-bg.png";
import Payment from "./payment";
import TransactionHistory from "./History";
import { makeStyles } from "@material-ui/core/styles";
import { useTourGuide } from "../../../hooks/useTourGuide";
import CountryGateModal from "../../../components/CountryGateModal";
import { isCountryMissing } from "../../../utils/countryHelpers";

const subMenuOptions = [
  ["add funds", "/wallet/add"],
  ["withdrawals", "/wallet/withdraw"],
  ["transaction history", "/wallet/history"],
];

const Wallet = () => {
  useTourGuide();
  const dispatch = useDispatch();
  const classes = useStyles();
  const { pathname } = useLocation();
  const { userData } = useSelector((state) => state.auth);
  const [type, setType] = useState("stripe");
  const [showCountryModal, setShowCountryModal] = useState(false);

  const { profile } = useSelector((store) => store);
  const { profileData } = profile;
  const { balance } = useSelector((state) => state.wallet);
  const { rates, currency } = useSelector((state) => state.currencyRate);
  const USD = rates.USD;
  const currencyRate = rates[currency];

  const currentCountry = profileData?.country || userData?.country;
  const hasValidCountry = !isCountryMissing(currentCountry);

  const walletCurrency = String(currentCountry) === "160" ? "NGN" : "USD";
  const [selectedCurrency, setSelectedCurrency] = useState(walletCurrency);
  const [balanceInLocal, setBalanceInLocal] = useState({});

  useEffect(() => {
    setSelectedCurrency(walletCurrency);
  }, [walletCurrency]);

  const activeWallet =
    balance && Array.isArray(balance)
      ? balance.find((w) => w.currency === selectedCurrency) || balance[0]
      : null;

  useEffect(() => {
    if (activeWallet) {
      const availableBalanceInBase = activeWallet.available / USD;
      const balanceInBase = activeWallet.balance / USD;
      setBalanceInLocal({
        available: availableBalanceInBase * currencyRate,
        balance: balanceInBase * currencyRate,
      });
    }
  }, [activeWallet, USD, currencyRate]);

  useEffect(() => {
    if (selectedCurrency === "NGN") {
      setType("jaraepay");
    } else {
      setType("stripe");
    }
  }, [selectedCurrency]);

  useEffect(() => {
    Promise.resolve(getAccountBalance(dispatch));
    Promise.resolve(getCurrencyRates(dispatch));
  }, [dispatch]);

  const updateBalance = () => {
    getAccountBalance(dispatch);
  };

  const currencySymbol = activeWallet?.currency === "NGN" ? "₦" : "$";

  return (
    <Layout>
      <div className="wallet d-flex flex-column flex-md-row mt-3">

        {/* Sidebar — unchanged */}
        <div className="sub-menu-container">
          <BackButton />
          <SubMenu menuOptions={subMenuOptions} />
        </div>

        {/* Main content */}
        <div className="main-content ml-md-4">

          {/* No country set */}
          {!hasValidCountry && (
            <div className="d-flex flex-column justify-content-center align-items-center py-5 text-center">
              <EmptyState text="Please select your country to activate your wallet and view your balances." />
              <button
                type="button"
                className="btn btn-primary mt-3"
                style={{
                  background: "#2563eb",
                  color: "#ffffff",
                  padding: "0.625rem 1.5rem",
                  borderRadius: "8px",
                  fontWeight: 600,
                  border: "none",
                }}
                onClick={() => setShowCountryModal(true)}
              >
                Activate Wallet
              </button>
            </div>
          )}

          {hasValidCountry && (
            <Card noPadding>

              {/* ── Balance carousel ── */}
              <div className={classes.carouselWrap}>
                <Carousel>

                  {/* Slide 1 — Available Balance */}
                  <Carousel.Item>
                    <div className={classes.balanceSlide}>
                      <div className={classes.balanceLabel}>Available Balance</div>
                      <div className={classes.balanceRow}>
                        <span className={classes.currencySymbol}>{currencySymbol}</span>
                        <span className={classes.balanceAmount}>
                          {numberWithCommas(
                            activeWallet
                              ? parseFloat(activeWallet.available).toFixed(2)
                              : "0.00"
                          )}
                        </span>
                      </div>
                      <div className={classes.balanceNote}>
                        Subject to local currency changes
                      </div>
                    </div>
                  </Carousel.Item>

                  {/* Slide 2 — Book Balance */}
                  <Carousel.Item>
                    <div className={classes.balanceSlide}>
                      <div className={classes.balanceLabel}>Book Balance</div>
                      <div className={classes.balanceRow}>
                        <span className={classes.currencySymbol}>{currencySymbol}</span>
                        <span className={classes.balanceAmount}>
                          {numberWithCommas(
                            activeWallet
                              ? parseFloat(activeWallet.balance).toFixed(2)
                              : "0.00"
                          )}
                        </span>
                      </div>
                      <div className={classes.balanceNote}>
                        Subject to local currency changes
                      </div>
                    </div>
                  </Carousel.Item>

                </Carousel>
              </div>

              {/* ── Section divider ── */}
              <div className={classes.divider} />

              {/* ── Content sections — routing logic unchanged ── */}
              {!pathname.includes("withdraw") &&
                !pathname.includes("history") &&
                !pathname.includes("add") && (
                  <Payment
                    type={type}
                    selectedCurrency={selectedCurrency}
                    updateBalance={updateBalance}
                  />
                )}
              {pathname.includes("add") && (
                <Payment
                  type={type}
                  selectedCurrency={selectedCurrency}
                  updateBalance={updateBalance}
                />
              )}
              {pathname.includes("withdraw") && (
                <Withdraw type={type} selectedCurrency={selectedCurrency} />
              )}
              {pathname.includes("history") && <TransactionHistory />}

            </Card>
          )}
        </div>
      </div>
      <CountryGateModal
        open={showCountryModal}
        onClose={() => setShowCountryModal(false)}
        onSuccess={() => {
          setShowCountryModal(false);
          getAccountBalance(dispatch);
        }}
        allowDismiss={true}
      />
    </Layout>
  );
};

const useStyles = makeStyles({
  carouselWrap: {
    borderRadius: "12px 12px 0 0",
    overflow: "hidden",
    "& .carousel-control-prev, & .carousel-control-next": {
      width: "40px",
      opacity: 0.6,
    },
    "& .carousel-control-prev-icon, & .carousel-control-next-icon": {
      width: "16px",
      height: "16px",
    },
    "& .carousel-indicators": {
      marginBottom: "12px",
    },
    "& .carousel-indicators li": {
      width: "18px",
      height: "3px",
      borderRadius: "2px",
      background: "rgba(255,255,255,0.4)",
      border: "none",
    },
    "& .carousel-indicators .active": {
      background: "#ffffff",
      opacity: 1,
    },
  },
  balanceSlide: {
    background: "#3A6ED4",
    height: "160px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px 24px 28px",
    textAlign: "center",
  },
  balanceLabel: {
    fontSize: "13px",
    fontWeight: 400,
    color: "rgba(255,255,255,0.7)",
    marginBottom: "8px",
    letterSpacing: "0.02em",
  },
  balanceRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "center",
    gap: "3px",
  },
  currencySymbol: {
    fontSize: "22px",
    fontWeight: 500,
    color: "rgba(255,255,255,0.9)",
    lineHeight: 1,
  },
  balanceAmount: {
    fontSize: "36px",
    fontWeight: 700,
    color: "#ffffff",
    lineHeight: 1,
    letterSpacing: "-0.5px",
  },
  balanceNote: {
    fontSize: "10px",
    color: "rgba(255,255,255,0.4)",
    marginTop: "10px",
  },
  divider: {
    height: "1px",
    background: "#F3F4F6",
  },
});

export default Wallet;